<script setup>
import { ref } from "vue";
import Button from './Button.vue';
import CoachApplication from "../elements/CoachApplication.vue";
const props = defineProps({
  element: {
    type: Object,
  },
})
const steps = ref([
  {
    number: '1',
    title: 'SIGN UP ONLINE',
    description: 'Submit your application and tell us a bit about yourself.'
  },
  {
    number: '2', 
    title: 'GET VERIFIED',
    description: 'Our team reviews your info and confirms your intended area is available.'
  },
  {
    number: '3',
    title: 'COMPLETE YOUR TRAINING',
    description: 'Attend hands-on live virtual workshops to master the techniques.'
  },
  {
    number: '4',
    title: 'START COACHING',
    description: 'Step into your first class, make an impact, and start earning extra income.'
  }
]);
</script>

<template>
  <!-- How to Become Coach Section -->
  <section class="how-to-become-coach-section">
    <div class="container">
      <div class="content-wrapper">
        <h2 class="section-title">
          <span class="red-text">HOW TO BECOME</span> AN ILKB C.O.A.C.H.
        </h2>
        
        <p class="section-description">
          Becoming an ILKB COACH is easier than you think. Our certification 
          program gives you all the training, tools, and support you need to start 
          coaching confidently—whether you want a full-time career or a flexible 
          side hustle. Follow these simple steps and get ready to make an impact.
        </p>

        <div class="steps-grid">
          <div 
            v-for="step in steps" 
            :key="step.number"
            class="step-item"
          >
            <div class="step-number">{{ step.number }}</div>
            <div class="step-content">
              <h3 class="step-title">{{ step.title }}</h3>
              <p class="step-description">{{ step.description }}</p>
            </div>
          </div>
        </div>

        <div class="cta-section">
          <CoachApplication :element="element"/>  
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.how-to-become-coach-section {

  color: white;

  position: relative;
  overflow: hidden;

  // Add torn paper effect at top and bottom
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    height: 20px;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 20"><path d="M0,0 L50,15 L100,5 L150,18 L200,8 L250,12 L300,3 L350,16 L400,6 L450,14 L500,4 L550,17 L600,7 L650,11 L700,2 L750,15 L800,9 L850,13 L900,5 L950,18 L1000,8 L1050,16 L1100,6 L1150,14 L1200,4 L1200,0 Z" fill="white"/></svg>') repeat-x;
    background-size: 200px 20px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    height: 20px;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 20"><path d="M0,20 L50,5 L100,15 L150,2 L200,12 L250,8 L300,17 L350,4 L400,14 L450,6 L500,16 L550,3 L600,13 L650,9 L700,18 L750,5 L800,11 L850,7 L900,15 L950,2 L1000,12 L1050,4 L1100,14 L1150,6 L1200,16 L1200,20 Z" fill="white"/></svg>') repeat-x;
    background-size: 200px 20px;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;
  }

  .content-wrapper {
    background: white;
    color: #333;
    padding: 3rem;
    border-radius: 0;
    position: relative;
    margin: 2rem 0;

    // Add torn paper effect to content wrapper
    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: 0;
      right: 0;
      height: 16px;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 16"><path d="M0,0 L40,12 L80,4 L120,14 L160,6 L200,10 L240,2 L280,13 L320,5 L360,11 L400,3 L440,14 L480,6 L520,9 L560,1 L600,12 L640,7 L680,10 L720,4 L760,14 L800,6 L840,13 L880,5 L920,11 L960,3 L1000,14 L1040,8 L1080,12 L1120,5 L1160,11 L1200,3 L1200,0 Z" fill="white"/></svg>') repeat-x;
      background-size: 160px 16px;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      right: 0;
      height: 16px;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 16"><path d="M0,16 L40,4 L80,12 L120,2 L160,10 L200,6 L240,14 L280,3 L320,11 L360,5 L400,13 L440,2 L480,10 L520,7 L560,15 L600,4 L640,9 L680,6 L720,12 L760,2 L800,10 L840,3 L880,11 L920,5 L960,13 L1000,2 L1040,8 L1080,4 L1120,11 L1160,5 L1200,13 L1200,16 Z" fill="white"/></svg>') repeat-x;
      background-size: 160px 16px;
    }
  }

  .section-title {
    font-size: 2.5rem;
    font-weight: 900;
    margin-bottom: 1.5rem;
    text-align: left; // Changed to left align to match "Why Coach"
    color: #333;
    text-transform: uppercase;
    letter-spacing: 1px;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;

    .red-text {
      color: #ff3b3f;
    }
  }

  .section-description {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 3rem;
    text-align: left; // Changed to left align to match "Why Coach"
    color: #333;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 3rem;
  }

  .steps-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
    margin-bottom: 3rem;
    max-width: 800px; // Match the text content width
    margin-left: auto;
    margin-right: auto; // Center the grid properly
  }

  .step-item {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
  }

  .step-number {
    color: #ff3b3f;
    font-size: 4.5rem; // Increased height
    font-weight: 900;
    font-family: Arial, sans-serif; // Reduced boldness - removed Arial Black
    line-height: 1.2; // Increased line height for better spacing
    flex-shrink: 0;
    margin-top: -0.5rem;
  }

  .step-content {
    flex: 1;
  }

  .step-title {
    font-size: 1.3rem;
    font-weight: 900;
    margin-bottom: 0.5rem;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .step-description {
    font-size: 1rem;
    line-height: 1.5;
    color: #555;
    margin: 0;
  }

  .cta-section {
    text-align: left;
    margin-top: 3rem;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;

    // Override the centered text alignment from CoachApplication component
    :deep(.coach-application-container) {
      text-align: left !important;
    }

    // Style the apply button
    :deep(.apply-now-btn) {
      background: #ff3b3f !important;
      color: white !important;
      padding: 1rem 2.5rem !important;
      font-size: 1.1rem !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      letter-spacing: 1px !important;
      border: none !important;
      border-radius: 0 !important;
      cursor: pointer !important;
      transition: all 0.3s ease !important;
      display: inline-block !important;
      margin: 0 !important;

      &:hover {
        background: #e63439 !important;
        transform: translateY(-2px) !important;
      }
    }
  }

  // Mobile responsive
  @media (max-width: 768px) {
    .content-wrapper {
      padding: 2rem 1.5rem;
      margin: 1rem;
    }

    .section-title {
      font-size: 2rem;
    }

    .section-description {
      font-size: 1rem;
    }

    .steps-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }

    .step-number {
      font-size: 3rem;
    }

    .step-title {
      font-size: 1.1rem;
    }

    .step-description {
      font-size: 0.9rem;
    }
  }
}
</style>